import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Alert,
  Typography,
} from '@mui/material';
import { firmAPI } from '../services/api';

interface VendorReassignDialogProps {
  open: boolean;
  onClose: () => void;
  onReassign: (data: {
    vendor_id: number;
    vendor_user_id?: number;
    due_date?: string;
    reassignment_reason: string;
  }) => Promise<void>;
  currentVendor?: {
    id: number;
    name: string;
  };
  currentVendorUser?: {
    id: number;
    name: string;
  };
}

const VendorReassignDialog: React.FC<VendorReassignDialogProps> = ({
  open,
  onClose,
  onReassign,
  currentVendor,
  currentVendorUser,
}) => {
  const [vendors, setVendors] = useState<any[]>([]);
  const [vendorUsers, setVendorUsers] = useState<any[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<any>(null);
  const [selectedVendorUser, setSelectedVendorUser] = useState<any>(null);
  const [dueDate, setDueDate] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      loadVendors();
    }
  }, [open]);

  useEffect(() => {
    if (selectedVendor) {
      loadVendorUsers(selectedVendor.id);
    } else {
      setVendorUsers([]);
      setSelectedVendorUser(null);
    }
  }, [selectedVendor]);

  const loadVendors = async () => {
    try {
      const res = await firmAPI.getAll({ type: 'vendor', status: 'approved', per_page: 1000 });
      const vendorList = res.data.data?.data || res.data.data || [];
      setVendors(vendorList);
    } catch (err) {
      console.error('Failed to load vendors:', err);
    }
  };

  const loadVendorUsers = async (vendorId: number) => {
    try {
      const res = await firmAPI.members(vendorId);
      setVendorUsers(res.data.data || []);
    } catch (err) {
      console.error('Failed to load vendor users:', err);
      setVendorUsers([]);
    }
  };

  const handleSubmit = async () => {
    if (!selectedVendor) {
      setError('Please select a vendor');
      return;
    }
    // Only require reason for reassignment, not initial assignment
    if (!isInitialAssignment && !reason.trim()) {
      setError('Please provide a reason for reassignment');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onReassign({
        vendor_id: selectedVendor.id,
        vendor_user_id: selectedVendorUser?.id,
        due_date: dueDate || undefined,
        reassignment_reason: reason,
      });
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to reassign vendor');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedVendor(null);
    setSelectedVendorUser(null);
    setDueDate('');
    setReason('');
    setError('');
    onClose();
  };

  const isInitialAssignment = !currentVendor;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isInitialAssignment ? 'Assign Vendor' : 'Reassign Vendor'}</DialogTitle>
      <DialogContent>
        {currentVendor && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Currently Assigned To:
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {currentVendor.name}
            </Typography>
            {currentVendorUser && (
              <Typography variant="body2" color="text.secondary">
                User: {currentVendorUser.name}
              </Typography>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Autocomplete
            options={vendors}
            getOptionLabel={(option) => option.name || ''}
            value={selectedVendor}
            onChange={(_, newValue) => setSelectedVendor(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="New Vendor *"
                placeholder="Select a vendor"
              />
            )}
          />

          {selectedVendor && (
            <Autocomplete
              options={vendorUsers}
              getOptionLabel={(option) => `${option.name} (${option.email})` || ''}
              value={selectedVendorUser}
              onChange={(_, newValue) => setSelectedVendorUser(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vendor User (Optional)"
                  placeholder="Select a user"
                />
              )}
            />
          )}

          <TextField
            label="Due Date (Optional)"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label={isInitialAssignment ? "Assignment Notes (Optional)" : "Reason for Reassignment *"}
            multiline
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={isInitialAssignment ? "Add any notes about this assignment..." : "Explain why this case is being reassigned..."}
            required={!isInitialAssignment}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedVendor || (!isInitialAssignment && !reason.trim())}
        >
          {loading 
            ? (isInitialAssignment ? 'Assigning...' : 'Reassigning...') 
            : (isInitialAssignment ? 'Assign Vendor' : 'Reassign Vendor')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VendorReassignDialog;

